import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Table,
  Popconfirm,
  Spin,
  Flex,
  Image,
  Tag,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import LocationSelector from "../../components/LocationSelector";
import {
  useFetchCleaningLogsQuery,
  useDeleteCleaningLogMutation,
} from "./cleaning.service";
import { BACKEND_URL } from "../../constant";
import moment from "moment";
import "../incident/style.css";

const { Column } = Table;

export default function CleaningLogPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.app.user);
  const [locationId, setLocationId] = useState(null);

  const canManageItems =
    user?.role === "admin" || user?.role === "local_admin";

  useEffect(() => {
    setLocationId(user?.activeLocationId || user?.restaurantId);
  }, [user]);

  const { data = { data: { logs: [] } }, isLoading, refetch } =
    useFetchCleaningLogsQuery(locationId, {
      skip: !locationId,
      refetchOnMountOrArgChange: true,
    });

  const logs = data?.data?.logs || [];

  const [deleteLog] = useDeleteCleaningLogMutation();

  const handleDelete = async (id) => {
    await deleteLog({ id, locationId });
    refetch();
  };

  return (
    <Spin spinning={isLoading}>
      <Card
        className="incident-page"
        bordered={false}
        title={
          <Flex align="center" gap="middle" wrap>
            <span>{t("Cleaning log")}</span>
            <LocationSelector onLocationChange={setLocationId} />
          </Flex>
        }
        extra={
          <Flex gap="small">
            {canManageItems && (
              <Button onClick={() => navigate("/cleaning/items")}>
                {t("Cleaning items")}
              </Button>
            )}
            <Button
              type="primary"
              onClick={() => navigate("/cleaning/create")}
              disabled={!locationId}
            >
              {t("Create")}
            </Button>
          </Flex>
        }
      >
        <Table
          dataSource={logs}
          rowKey="_id"
          pagination={{ pageSize: 10 }}
          locale={{
            emptyText: locationId
              ? t("No cleaning records found")
              : t("Please select a location"),
          }}
        >
          <Column title={t("Item cleaned")} dataIndex="itemName" />
          <Column title={t("Cleaned by")} dataIndex="cleanedByName" />
          <Column
            title={t("Date & time")}
            dataIndex="cleanedAt"
            render={(value) =>
              value ? moment(value).format("MMM D, YYYY - hh:mm A") : "-"
            }
          />
          <Column
            title={t("Status")}
            dataIndex="status"
            render={(status) => <Tag color="green">{status || "Completed"}</Tag>}
          />
          <Column title={t("Notes")} dataIndex="notes" />
          <Column
            title={t("Photo")}
            dataIndex="photoUrl"
            render={(photoUrl) =>
              photoUrl ? (
                <Image
                  width={48}
                  height={48}
                  src={
                    photoUrl.startsWith("http")
                      ? photoUrl
                      : `${BACKEND_URL}/${photoUrl}`
                  }
                  style={{ objectFit: "cover", borderRadius: 4 }}
                />
              ) : (
                "-"
              )
            }
          />
          <Column
            title={t("TOOLS")}
            align="center"
            render={(_, record) => (
              <Popconfirm
                title={t("Delete cleaning record")}
                description={t("Are you sure you want to delete this record?")}
                onConfirm={() => handleDelete(record._id)}
                okText={t("Yes")}
                cancelText={t("No")}
              >
                <DeleteOutlined style={{ color: "red", cursor: "pointer" }} />
              </Popconfirm>
            )}
          />
        </Table>
      </Card>
    </Spin>
  );
}
